import { useState, useEffect, useMemo } from 'react';
import { WORKFLOW_LABELS } from '../lib/billWorkflow';
import { Link } from 'react-router-dom';
import {
  ReceiptText, AlertTriangle, CheckCircle2, Clock, Eye, ExternalLink,
  X, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, RefreshCw, Archive,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader,
} from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { FilterDropdown } from '../components/common/FilterDropdown';
import { DateRangeFilter } from '../components/common/DateRangeFilter';
import { BillFilterBar } from '../components/common/BillFilterBar';
import { BulkStatusSelect } from '../components/bills/BulkStatusSelect';
import { WorkflowStepPicker } from '../components/bills/WorkflowStepPicker';
import { billService } from '../services/billService';
import { inDateRange, type DateBasis } from '../utils/billDateFilter';
import {
  BILL_PARSE_ORDER, BILL_PARSE_LABEL, BILL_PARSE_BADGE_CLS, billParseStateOf, parseSubjectsByBill,
} from '../utils/billParseStatus';
import { overriddenBills, summarizeRefresh } from '../utils/billRefreshSummary';
import { billStatus, STATUS_ORDER, type BillStatus } from '../utils/billStatus';
import { useBillVerdicts } from '../hooks/useBillVerdicts';
import { useBillFilters } from '../hooks/useBillFilters';
import { useBoardRows } from '../hooks/useBoardRows';
import { useWorkflowStep } from '../hooks/useWorkflowStep';
import {
  NO_BILL_FILTERS, billFilterOptions, hasActiveBillFilters, matchesBillFilters, type BillFilterContext,
} from '../lib/billFilters';
import { NO_SENDS, reportSendStatus, type ReportSendStatus } from '../lib/reportStatus';
import { ArchiveInvoiceDialog } from '../components/ArchiveInvoiceDialog';
import type { ArchiveReason } from '../utils/archiveReasons';
import { Checkbox } from '../../../foundation/altzero_base/base/components/ui/checkbox';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../foundation/altzero_base/base/components/ui/dropdown-menu';
import { parseErrorOf } from '../utils/parseState';
import type { ParseSubjectLike, ParseState } from '../utils/parseState';
import type { BvBill } from '../types/billTypes';

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU');
}

function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

const PAGE_SIZE = 50;

// ─── Status ───────────────────────────────────────────────────────────────────

// The badge and the Status filter must agree on what a bill's status *is*, so
// the classification lives in utils/billStatus.ts and both this page and its
// tests read it from there. Two copies of this ladder would let a bill show
// "Overcharge" and not appear under that filter.

const STATUS_META: Record<BillStatus, { label: string; cls: string; icon: typeof Clock }> = {
  pending:     { label: 'Pending',     cls: 'bg-gray-100 text-gray-500',     icon: Clock },
  overcharge:  { label: 'Overcharge',  cls: 'bg-red-100 text-red-700',       icon: AlertTriangle },
  undercharge: { label: 'Undercharge', cls: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
  matched:     { label: 'Within range',     cls: 'bg-green-100 text-green-700',   icon: CheckCircle2 },
  warnings:    { label: 'Warnings',    cls: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
  calculated:  { label: 'Calculated',  cls: 'bg-green-100 text-green-700',   icon: CheckCircle2 },
  archived:    { label: 'Archived',    cls: 'bg-gray-100 text-gray-500',     icon: Archive },
};

// Parse status is a different question to the Status above: that one is about
// the variance once a bill has been read, this one is about whether reading it
// worked at all. A bill sits at "Pending" for both a parse that is still running
// and one that died three weeks ago, and only this filter separates them.
const PARSE_OPTIONS = BILL_PARSE_ORDER.map((s) => BILL_PARSE_LABEL[s]);

function StatusBadge({ bill, status }: { bill: BvBill; status: BillStatus }) {
  const { label, cls, icon: Icon } = STATUS_META[status];
  const count = status === 'warnings' ? (bill.shadow_result?.warnings ?? []).length : 0;
  return (
    <span data-testid="bill-status-badge" data-status={status} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" /> {label}{count ? ` (${count})` : ''}
    </span>
  );
}

// The bills load before the invoice emails they derive their parse state from,
// and the email fetch is allowed to fail without taking the page with it. Both
// cases arrive here as `undefined`, and both mean the same thing to a reader:
// not known. A dash says that; an amber "Not parsed" would be a claim.
function ParseBadge({ state, error }: { state?: ParseState; error?: string | null }) {
  if (!state) return <span data-testid="bill-parse-badge" data-parse-state="unknown" className="text-gray-300">—</span>;
  // Job 76, on the Bills list: a bare "Parse failed" sent the user into the site
  // page and its Invoices tab to read a reason that is already loaded here. The
  // stalled fallback matches ParseStatusPage — a parse killed mid-run records
  // nothing, and silence there reads as "no reason exists".
  const reason = state === 'failed'
    ? (error ?? 'No reason recorded — use Retry parse.')
    : state === 'stalled'
      ? (error ?? 'The parse stopped without recording a reason — most likely the server restarted mid-parse.')
      : null;
  return (
    <>
      <span data-testid="bill-parse-badge" data-parse-state={state} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${BILL_PARSE_BADGE_CLS[state]}`}>
        {BILL_PARSE_LABEL[state]}
      </span>
      {reason && (
        <p
          className={cn('mt-1 max-w-[260px] text-[11px] leading-snug break-words',
            state === 'failed' ? 'text-red-600' : 'text-amber-700')}
          title={reason}
        >
          {reason}
        </p>
      )}
    </>
  );
}

// ─── Sorting ──────────────────────────────────────────────────────────────────

type SortField =
  | 'nmi' | 'entity' | 'period' | 'days' | 'kwh'
  | 'expected' | 'actual' | 'status' | 'parse' | 'created_at';

// `parseState` is passed in rather than derived here: it depends on the invoice
// emails loaded alongside the bills, which a module-level function cannot reach.
function sortValue(
  bill: BvBill,
  field: SortField,
  parseState: ParseState | undefined,
  status: BillStatus | undefined,
): string | number | null {
  switch (field) {
    case 'nmi':      return bill.site?.nmi ?? null;
    case 'entity':   return bill.site?.client?.name ?? null;
    case 'period':   return bill.bill_from ?? null;
    case 'days':     return bill.billing_days ?? null;
    case 'kwh':      return bill.total_kwh ?? null;
    case 'expected': return bill.shadow_result?.total ?? null;
    case 'actual':   return bill.actual_total ?? null;
    case 'status':   return STATUS_ORDER.indexOf(status ?? billStatus(bill));
    case 'parse':    return parseState ? BILL_PARSE_ORDER.indexOf(parseState) : null;
    case 'created_at': return bill.created_at;
  }
}

// ─── URL params only this list has ────────────────────────────────────────────

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** A parse-status key from the URL, or '' (unknown values are dropped). */
const parseKeyOf = (value: string): ParseState | '' =>
  ((BILL_PARSE_ORDER as string[]).includes(value) ? (value as ParseState) : '');


export function BillsListPage() {
  const [bills, setBills] = useState<BvBill[]>([]);
  const [parseSubjects, setParseSubjects] = useState<Map<string, ParseSubjectLike>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The filters every bill list shares live in the URL (hooks/useBillFilters),
  // and so do this list's own: parse status and the period/created date range.
  const { filters, query, setFilters, update, param } = useBillFilters();
  const parseFilter = parseKeyOf(param('parse'));
  const dateBasis: DateBasis = param('basis') === 'created' ? 'created' : 'period';
  const dateFrom = DAY_KEY.test(param('dfrom')) ? param('dfrom') : '';
  const dateTo = DAY_KEY.test(param('dto')) ? param('dto') : '';

  // Selection is by id and survives filtering and paging — narrowing the list to
  // find the next bill you want must not silently drop the ones already picked.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState<string | null>(null);
  const [refreshNotice, setRefreshNotice] = useState<string | null>(null);

  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Archive dialog state for the bulk "Archive selected" action.
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // A partial archive/restore failure is not a page-load failure — it must
  // not render through `error`, which replaces the whole table with the
  // full-page error screen. This renders as an inline banner above the table
  // instead, so the rows that did succeed stay visible.
  const [actionError, setActionError] = useState<string | null>(null);

  // Job 81: fetch every scope, not just active. Archived bills are the only
  // thing on this page that is fetched but hidden — every view below filters
  // them out client-side except Status = Archived, which is the one place
  // they have to be reachable.
  const loadBills = async () => {
    const [billsRes, emailsRes] = await Promise.all([
      billService.listAll('all'), billService.listInvoiceEmails(),
    ]);
    if (billsRes.success) setBills(billsRes.data ?? []);
    else setError(billsRes.error ?? 'Failed to load bills');
    // A failed email fetch is not a failed page. The parse status of a bill
    // with an emailed invoice falls back to what its own row says, which is
    // coarser but never wrong — better than blanking a list of bills over a
    // filter that most visits never touch.
    if (emailsRes.success) setParseSubjects(parseSubjectsByBill(emailsRes.data ?? []));
  };

  useEffect(() => {
    loadBills().finally(() => setLoading(false));
  }, []);

  // Resolved once per load, not per render: `billParseStateOf` reads the clock,
  // and re-deriving it on every keystroke would let a row flip to "stalled"
  // mid-filter with nothing on screen explaining why it moved.
  const parseStateById = useMemo(() => {
    const now = Date.now();
    return new Map(bills.map((b) => [b.id, billParseStateOf(b, parseSubjects.get(b.id), now)]));
  }, [bills, parseSubjects]);

  // One verdict per bill, read through its retailer's charge map (billVerdict),
  // shared by the badge, the Verdict filter and the sort so they cannot disagree.
  const verdictFor = useBillVerdicts(bills);
  const statusById = useMemo(
    () => new Map(bills.map((b) => [b.id, billStatus(b, verdictFor(b))] as const)),
    [bills, verdictFor],
  );
  // Each active bill's board row (status, report bucket, send status), worked
  // out exactly as the Bulk Bill Run board does. Secondary: without it the
  // Status column reads '—'. Archived bills are not on the board: no status.
  const board = useBoardRows(verdictFor);
  const { saving: savingStep, error: stepError, setStep } = useWorkflowStep(board.reload);
  // Send status for the shared filter: the board's, else (archived bills) from
  // the send history; null when that history did not load.
  const sendById = useMemo(
    () => new Map(bills.map((b): readonly [string, ReportSendStatus | null] => {
      const row = board.rowsById.get(b.id);
      if (row) return [b.id, row.sendStatus];
      return [b.id, board.sendHistory ? reportSendStatus(b, board.sendHistory[b.id] ?? NO_SENDS, verdictFor(b)) : null];
    })),
    [bills, board.rowsById, board.sendHistory, verdictFor],
  );

  // Job 81: archived bills are fetched but not shown by default, so the option
  // lists — like every other count on this page — are built from the bills a
  // normal visit actually sees. Otherwise an org that archives a client's last
  // bill grows an Entity option that filters to nothing.
  const activeBills = useMemo(() => bills.filter((b) => !b.archived_at), [bills]);

  // `parse_error` is written against the email's attachment, so a bill whose
  // invoice was uploaded by hand has no reason to show — the manual path drops
  // it (the open half of job 76, see ParseStatusPage's Reason).
  const parseErrorById = useMemo(() => {
    const entries: [string, string][] = [];
    for (const b of bills) {
      const subject = parseSubjects.get(b.id);
      const err = subject ? parseErrorOf(subject) : null;
      if (err) entries.push([b.id, err]);
    }
    return new Map(entries);
  }, [bills, parseSubjects]);

  // Options come from the loaded rows, so every value listed matches at least
  // one bill — a dropdown entry that filters to nothing is worse than no entry.
  const options = useMemo(() => billFilterOptions(activeBills), [activeBills]);

  // The header/footer counts reason about the same visibility scope as the
  // Status filter: everything except Archived hides archived rows, so the
  // "N of M" totals must too, or the headline count jumps the moment
  // listAll('all') starts pulling retired bills down.
  const visibleTotal = useMemo(
    () => bills.filter((b) => (filters.verdict === 'archived' ? !!b.archived_at : !b.archived_at)).length,
    [bills, filters.verdict],
  );

  const filtered = useMemo(() => bills.filter((b) => {
    const ctx: BillFilterContext = {
      verdict: statusById.get(b.id) ?? billStatus(b),
      workflow: board.rowsById.get(b.id)?.status ?? null,
      sendStatus: sendById.get(b.id) ?? null,
    };
    // Job 81's rule (archived only under Verdict = Archived) is in the shared
    // predicate: they are still fetched, because that is the one way to reach them.
    if (!matchesBillFilters(b, ctx, filters)) return false;
    if (parseFilter && parseStateById.get(b.id) !== parseFilter) return false;
    if (!inDateRange(b, dateBasis, dateFrom, dateTo)) return false;
    return true;
  }), [bills, statusById, board.rowsById, sendById, filters, parseFilter, parseStateById, dateBasis, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    // Blanks sort last in both directions. Under `desc` they would otherwise
    // fill the first page, which reads as "no data" on a column that has plenty.
    return [...filtered].sort((a, b) => {
      const va = sortValue(a, sortField, parseStateById.get(a.id), statusById.get(a.id));
      const vb = sortValue(b, sortField, parseStateById.get(b.id), statusById.get(b.id));
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb));
      return cmp * dir;
    });
  }, [filtered, sortField, sortDir, parseStateById, statusById]);

  const lastPage = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // A filter change can shrink the list below the current page. Clamp on render
  // rather than in an effect, so the page never blanks for a frame first.
  const safePage = Math.min(page, lastPage);
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // The basis on its own narrows nothing — only a bound does.
  const extraActive = !!(parseFilter || dateFrom || dateTo);
  const hasActiveFilters = hasActiveBillFilters(filters) || extraActive;

  const resetPage = () => setPage(1);
  // Any filter change goes back to page 1, including one made by Back/Forward:
  // the filters are in the URL.
  useEffect(() => { setPage(1); }, [query, parseFilter, dateBasis, dateFrom, dateTo]);

  const onSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    resetPage();
  };

  // The basis is a preference, not a filter, so it survives a clear — only the
  // bounds are reset. One URL write (see lib/billFilters.applyFilterUpdate).
  const clearAllFilters = () => update(NO_BILL_FILTERS, { parse: '', dfrom: '', dto: '' });

  // The header checkbox acts on the page in front of you, not on the whole
  // filtered set — "select all" that quietly picks up 800 off-screen rows is how
  // a bulk action becomes something nobody meant to run.
  const pageIds = pageRows.map((b) => b.id);
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const pageSomeSelected = pageIds.some((id) => selected.has(id));

  const toggleOne = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const togglePage = () => setSelected((prev) => {
    const next = new Set(prev);
    if (pageAllSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    return next;
  });

  // Every bulk action acts on what is ticked AND still shown under the filters;
  // ticked rows a filter hides are left alone (and counted in the toolbar).
  const selectedBills = filtered.filter((b) => selected.has(b.id));
  const activeSelectedIds = selectedBills.filter((b) => !b.archived_at).map((b) => b.id);
  const noneActed = selectedBills.length === 0;

  // Refresh rebuilds what the validation report derives: usage from the invoice,
  // then the actual total, then the expected invoice priced against that usage.
  // It does NOT re-read the PDF — that is a parse, and it costs minutes.
  const handleRefreshSelected = async () => {
    if (selectedBills.length === 0) return;

    // Job 52, in bulk: recalculating replaces a hand-corrected expected invoice.
    // Asked once for the whole selection rather than once per bill, and answered
    // by leaving those bills alone rather than by skipping the run.
    const edited = overriddenBills(selectedBills);
    let replaceOverridden = false;
    if (edited.length > 0) {
      replaceOverridden = confirm(
        `${edited.length} of the selected bills have an expected invoice that was edited by hand.\n\n`
        + 'OK replaces those edits with freshly calculated values.\n'
        + 'Cancel refreshes everything else and leaves them exactly as they are.',
      );
    }

    setRefreshing(true);
    setRefreshNotice(null);
    setRefreshProgress(`Refreshing ${selectedBills.length} bill${selectedBills.length === 1 ? '' : 's'}…`);
    try {
      const res = await billService.refreshBills(selectedBills.map((b) => b.id), { replaceOverridden });
      if (!res.success || !res.data) {
        setRefreshNotice(res.error ?? 'Refresh failed');
        return;
      }
      // Replace the refreshed rows in place. A full reload would throw away the
      // filters and the page position that got the user to this selection.
      const byId = new Map(res.data.bills.map((b) => [b.id, b]));
      setBills((prev) => prev.map((b) => byId.get(b.id) ?? b));
      setRefreshNotice(summarizeRefresh(res.data.outcomes));
      board.reload();
    } catch (err) {
      setRefreshNotice(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRefreshProgress(null);
      setRefreshing(false);
    }
  };

  // Job 81: bulk archive from the Actions menu, reusing the same confirm dialog
  // the Invoices tab uses rather than a second copy of it.
  const handleArchiveSelected = async (reason: ArchiveReason, note: string) => {
    const acted = new Set(selectedBills.map((b) => b.id));
    setArchiveBusy(true);
    setActionError(null);
    const res = await billService.archive(selectedBills.map((b) => b.id), reason, note);
    setArchiveBusy(false);
    // Reload either way: a partial failure still moved rows, and leaving the
    // table showing them as active is worse than the error itself.
    await loadBills();
    board.reload();
    if (!res.success) { setArchiveError(res.error ?? 'Archive failed'); return; }
    setArchiveOpen(false);
    setSelected((prev) => new Set([...prev].filter((id) => !acted.has(id))));
  };

  const handleRestoreSelected = async () => {
    const acted = new Set(selectedBills.map((b) => b.id));
    setActionError(null);
    const res = await billService.restore(selectedBills.map((b) => b.id));
    await loadBills();
    board.reload();
    if (!res.success) { setActionError(res.error ?? 'Restore failed'); return; }
    setSelected((prev) => new Set([...prev].filter((id) => !acted.has(id))));
  };

  const th = (label: string, field: SortField, align: 'left' | 'right' = 'left') => (
    <th
      data-testid={`bills-th-${field}`}
      data-sort={sortField === field ? sortDir : 'none'}
      className={`px-6 py-3 cursor-pointer select-none hover:text-gray-700 whitespace-nowrap ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onSort(field)}
    >
      {label}
      {sortField !== field
        ? <ChevronsUpDown className="h-3 w-3 ml-1 opacity-30 inline" />
        : sortDir === 'asc'
          ? <ChevronUp className="h-3 w-3 ml-1 text-blue-500 inline" />
          : <ChevronDown className="h-3 w-3 ml-1 text-blue-500 inline" />}
    </th>
  );

  if (loading) {
    return (
      <div data-testid="bills-loading" className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="h-6 w-40 bg-gray-100 animate-pulse rounded" />
        <div className="h-64 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  return (
    <div data-testid="bills-page" className="w-full py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ReceiptText className="h-6 w-6 text-primary" />
            Bills
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {hasActiveFilters
              ? `${sorted.length} of ${visibleTotal} bill${visibleTotal !== 1 ? 's' : ''}`
              : `${visibleTotal} bill${visibleTotal !== 1 ? 's' : ''} across all sites`}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <div className="flex flex-wrap items-center gap-2">
            <BillFilterBar
              filters={filters}
              onChange={setFilters}
              onClear={clearAllFilters}
              options={options}
              testIdPrefix="bills"
              searchPlaceholder="Search NMI or entity…"
              verdictOptions={STATUS_ORDER}
              extraActive={extraActive}
            >
              <FilterDropdown
                label="Parse status"
                value={parseFilter ? BILL_PARSE_LABEL[parseFilter] : ''}
                onChange={(label) => update({}, { parse: BILL_PARSE_ORDER.find((s) => BILL_PARSE_LABEL[s] === label) ?? '' })}
                options={PARSE_OPTIONS}
              />
              <DateRangeFilter
                basis={dateBasis}
                onBasisChange={(b) => update({}, { basis: b === 'created' ? 'created' : '' })}
                from={dateFrom}
                to={dateTo}
                onChange={(f, t) => update({}, { dfrom: f, dto: t })}
              />
            </BillFilterBar>

            {selected.size > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {selected.size} selected{selectedBills.length !== selected.size && ` (${selectedBills.length} shown)`}
                </span>
                {/* Active bills still shown under the filters: an archived bill has no status. */}
                <BulkStatusSelect
                  testId="bills-action-status"
                  disabled={savingStep || activeSelectedIds.length === 0}
                  onSet={(step) => void setStep(activeSelectedIds, step)}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="h-9 gap-1.5 text-xs" disabled={refreshing || noneActed}>
                      {refreshing
                        ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        : <ChevronDown className="h-3.5 w-3.5" />}
                      Actions
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleRefreshSelected} disabled={refreshing || noneActed}>
                      <RefreshCw className="h-3.5 w-3.5 mr-2" />
                      Refresh
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setArchiveError(null); setArchiveOpen(true); }}>
                      <Archive className="h-3.5 w-3.5 mr-2" />
                      Archive selected
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => void handleRestoreSelected()}>
                      <RefreshCw className="h-3.5 w-3.5 mr-2" />
                      Restore selected
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 text-xs text-gray-500 gap-1"
                  onClick={() => setSelected(new Set())}
                >
                  <X className="h-3.5 w-3.5" />
                  Clear selection
                </Button>
              </div>
            )}
          </div>

          {stepError && (
            <p data-testid="bills-step-error" className="mt-2 text-xs text-red-600">{stepError}</p>
          )}

          {(refreshProgress || refreshNotice) && (
            <p className={`mt-2 text-xs ${refreshProgress ? 'text-muted-foreground' : 'text-blue-700'}`}>
              {refreshProgress ?? refreshNotice}
            </p>
          )}

          {/* Period is a range, so an overlapping bill counts as in-range. Said
              out loud because the alternative reading — only bills fully inside
              the window — is just as plausible and gives a different answer. The
              picker already shows which dates are set, so this only has to
              explain the rule. */}
          {dateBasis === 'period' && (dateFrom || dateTo) && (
            <p className="mt-2 text-xs text-muted-foreground">
              Includes any bill whose period overlaps the selected dates, not only those falling entirely inside them.
            </p>
          )}
        </CardHeader>
        <CardContent className="p-0 mt-4">
          {/* Job 81: a partial archive/restore failure is not a page-load
              failure — it renders here, above the table, so the rows that did
              succeed (and every other bill) stay on screen rather than being
              replaced by the full-page error state below. */}
          {actionError && (
            <div
              data-testid="bills-action-error"
              className="mx-6 mb-4 flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              <span>{actionError}</span>
              <button
                type="button"
                onClick={() => setActionError(null)}
                className="shrink-0 text-red-400 hover:text-red-600"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {error ? (
            <div data-testid="bills-error" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <AlertTriangle className="h-10 w-10 mb-3 text-red-400" />
              <p className="text-sm">{error}</p>
            </div>
          ) : sorted.length === 0 ? (
            <div data-testid="bills-empty" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <ReceiptText className="h-10 w-10 mb-3 opacity-20" />
              <p className="text-sm font-medium">{hasActiveFilters ? 'No bills match your filters' : 'No bills yet'}</p>
              {hasActiveFilters ? (
                <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={clearAllFilters}>
                  Clear filters
                </Button>
              ) : (
                <p className="text-xs mt-1">Add a bill from a site's Bills tab</p>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table data-testid="bills-table" className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-gray-100">
                      <th className="pl-6 pr-2 py-3 w-10">
                        <Checkbox
                          checked={pageAllSelected ? true : pageSomeSelected ? 'indeterminate' : false}
                          onCheckedChange={togglePage}
                          aria-label={pageAllSelected ? 'Clear selection on this page' : 'Select every bill on this page'}
                        />
                      </th>
                      <th className="px-6 py-3 w-12">View</th>
                      {th('NMI', 'nmi')}
                      {th('Entity', 'entity')}
                      {th('Period', 'period')}
                      {th('Days', 'days')}
                      {th('Total kWh', 'kwh', 'right')}
                      {th('Expected Total', 'expected', 'right')}
                      {th('Actual Total', 'actual', 'right')}
                      <th data-testid="bills-th-workflow" className="px-6 py-3 whitespace-nowrap">Status</th>
                      {th('Verdict', 'status')}
                      {th('Parse', 'parse')}
                      {th('Created', 'created_at', 'right')}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {pageRows.map((bill) => (
                      <tr
                        key={bill.id}
                        data-testid="bills-row"
                        data-bill-id={bill.id}
                        className={cn('hover:bg-gray-50/50', selected.has(bill.id) && 'bg-blue-50/50')}
                      >
                        <td className="pl-6 pr-2 py-4">
                          <Checkbox
                            checked={selected.has(bill.id)}
                            onCheckedChange={() => toggleOne(bill.id)}
                            aria-label={`Select bill for NMI ${bill.site?.nmi ?? bill.id}`}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <Link
                            data-testid="bills-row-view"
                            to={`/bill-validation/bills/${bill.id}`}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                            title="View bill run — Cmd/Ctrl-click or middle-click for a new tab"
                            aria-label="View bill run"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                        </td>
                        <td className="px-6 py-4 font-mono font-medium text-gray-900">
                          {/* Job 17: the only clickable thing in this row used to be
                              the Eye icon, which opens the bill run — so clicking an
                              NMI to reach its site landed on a bill run instead. An
                              anchor, so Cmd/Ctrl-click and middle-click work too. */}
                          <span className="inline-flex items-center gap-1.5">
                            {bill.site_id ? (
                              <Link
                                to={`/bill-validation/sites/${bill.site_id}`}
                                state={{ from: { label: 'Back to Bills', to: '/bill-validation/bills' } }}
                                className="hover:text-primary hover:underline"
                                title="Open site — Cmd/Ctrl-click for a new tab"
                              >
                                {bill.site?.nmi ?? '—'}
                              </Link>
                            ) : (
                              bill.site?.nmi ?? '—'
                            )}
                            {/* Job 41: jump to the site's (or its client's) Shield
                                drive folder. Rendered only when a link exists — no
                                dead icon otherwise. */}
                            {(() => {
                              const drive = bill.site?.drive_link ?? bill.site?.client?.drive_link;
                              return drive ? (
                                <a
                                  href={drive}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-400 hover:text-primary"
                                  title="Open drive folder"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              ) : null;
                            })()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">
                          {bill.site?.client?.name ?? '—'}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">
                          {fmtDate(bill.bill_from)} – {fmtDate(bill.bill_to)}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">
                          {bill.billing_days ?? '—'}
                        </td>
                        <td className="px-6 py-4 text-right tabular-nums">
                          {bill.total_kwh != null ? `${bill.total_kwh.toLocaleString()} kWh` : '—'}
                        </td>
                        <td className="px-6 py-4 text-right tabular-nums font-medium">
                          {bill.shadow_result
                            ? fmtCurrency(bill.shadow_result.total)
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-6 py-4 text-right tabular-nums font-medium">
                          {bill.actual_total != null
                            ? fmtCurrency(bill.actual_total)
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td data-testid="bills-row-workflow" className="px-6 py-4 text-xs whitespace-nowrap">
                          {(() => {
                            const row = board.rowsById.get(bill.id);
                            if (!row) return <span className="text-gray-300">—</span>;
                            const wf = row.status;
                            return (
                              <>
                                <span className={cn('font-medium', wf === 'completed' ? 'text-emerald-700' : wf === 'mismatched' ? 'text-red-600' : wf === 'internal_review' || wf === 'system_review' ? 'text-amber-700' : 'text-gray-800')}>
                                  {WORKFLOW_LABELS[wf]}
                                </span>
                                <WorkflowStepPicker
                                  status={wf}
                                  bucket={row.bucket}
                                  step={row.bill.workflow_status ?? null}
                                  disabled={savingStep}
                                  onChange={(step) => void setStep([bill.id], step)}
                                />
                              </>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge bill={bill} status={statusById.get(bill.id) ?? billStatus(bill)} />
                        </td>
                        <td className="px-6 py-4">
                          <ParseBadge
                            state={parseStateById.get(bill.id)}
                            error={parseErrorById.get(bill.id) ?? null}
                          />
                        </td>
                        <td className="px-6 py-4 text-right text-xs text-muted-foreground whitespace-nowrap">
                          {fmtDate(bill.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 text-xs text-gray-500">
                <span>
                  {sorted.length} bill{sorted.length === 1 ? '' : 's'}
                  {hasActiveFilters && visibleTotal !== sorted.length && ` (filtered from ${visibleTotal})`}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, Math.min(p, lastPage) - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span data-testid="bills-page-info">Page {safePage} of {lastPage}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2"
                    disabled={safePage >= lastPage}
                    onClick={() => setPage((p) => Math.min(lastPage, Math.min(p, lastPage) + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ArchiveInvoiceDialog
        open={archiveOpen}
        count={selectedBills.length}
        busy={archiveBusy}
        error={archiveError}
        onCancel={() => { if (!archiveBusy) setArchiveOpen(false); }}
        onConfirm={(reason, note) => void handleArchiveSelected(reason, note)}
      />
    </div>
  );
}
